// Interactive practice activities for the Learn -> Practice -> Challenge model.
// Mic activities analyse pitch live in the browser only — nothing is recorded,
// stored or played back (see lib/pitch.js for the safeguarding contract).
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Mic, MicOff, Volume2, Star, RotateCcw } from "lucide-react";
import { StaveView } from "./Stave";
import { usePitch, MIC_NOTE, midiName } from "../lib/pitch";
import { playAudioSpec, vexKeyToMidi } from "../lib/audio";
import { childApi } from "../lib/api";
import { centsFromTarget, freqToNote, DEFAULT_A4 } from "../lib/tuning";

const TUNE_TOL = 0.45; // semitones either side of target that count as in tune

export function PracticeShell({ s, idx, Cmp, refA4 = DEFAULT_A4 }) {
  const { topicId } = useParams();
  const [won, setWon] = useState(false);
  const [awarded, setAwarded] = useState(0);
  const onWin = async () => {
    if (won) return;
    setWon(true);
    try {
      const r = await childApi.post("/child/learn/activity-win", { topic_id: topicId, activity: s.activity });
      if (r.data?.awarded) setAwarded(r.data.awarded);
    } catch { /* points are a bonus — never block practice */ }
  };
  return (
    <div className="space-y-3" data-testid={`practice-${s.activity}-${idx}`}>
      {s.practice && (
        <p className="text-xs font-bold uppercase tracking-widest text-[#2D6A4F] flex items-center gap-1.5">
          <Star className="w-4 h-4" aria-hidden /> Practice — try as many times as you like
        </p>
      )}
      <Cmp s={s} idx={idx} onWin={onWin} refA4={refA4} />
      {won && (
        <div className="rounded-xl bg-[#EBF6F0] border-2 border-[#2D6A4F] p-3 text-sm font-bold text-[#2D6A4F]" data-testid={`practice-win-${idx}`}>
          Well done!{awarded ? ` +${awarded} Note${awarded > 1 ? "s" : ""} earned.` : ""} Come back and practise this again any time.
        </div>
      )}
    </div>
  );
}

function MicGate({ error, start, testid }) {
  return (
    <div className="rounded-xl bg-[#F6EED8] border border-[#C69B3D]/40 p-4 space-y-3" data-testid={`${testid}-gate`}>
      <p className="text-sm text-[#1C242B] font-semibold flex items-center gap-2">
        <Mic className="w-4 h-4 text-[#C69B3D]" aria-hidden /> This activity listens to your singing.
      </p>
      <p className="text-xs text-[#566270]">{MIC_NOTE}</p>
      {error === "denied" && (
        <p className="text-xs text-[#A62B2B] font-semibold" data-testid={`${testid}-mic-error`}>
          The microphone is switched off for ChoirQuest. Ask a grown-up to allow it using the microphone icon in the address bar, then try again.
        </p>
      )}
      {error === "unavailable" && (
        <p className="text-xs text-[#A62B2B] font-semibold" data-testid={`${testid}-mic-error`}>
          No microphone found on this device — you can still do the listening activities, and come back to the singing ones on a device with a microphone.
        </p>
      )}
      <button onClick={start} data-testid={`${testid}-mic-start`}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px]">
        <Mic className="w-4 h-4" aria-hidden /> Start the microphone
      </button>
    </div>
  );
}

function PitchGauge({ pitch, targetMidi, refA4 = DEFAULT_A4 }) {
  // All pitch maths honour the choir's concert pitch (refA4), never hard-coded 440 Hz.
  const live = pitch ? freqToNote(pitch.freq, refA4) : null;
  const offCents = pitch ? centsFromTarget(pitch.freq, targetMidi, refA4) : null;
  const clamped = offCents === null ? 0 : Math.max(-50, Math.min(50, offCents));
  const inTune = offCents !== null && Math.abs(offCents) <= TUNE_TOL * 100;
  return (
    <div className="space-y-2" data-testid="pitch-gauge">
      <div className="flex items-end justify-between">
        <p className="font-display text-3xl font-bold text-[#0B192C]" data-testid="pitch-detected">{live ? live.name : "—"}</p>
        <p className={`text-sm font-bold ${offCents === null ? "text-[#566270]" : inTune ? "text-[#2D6A4F]" : "text-[#A62B2B]"}`} data-testid="pitch-verdict">
          {offCents === null ? "Sing a steady note…" : inTune ? "In tune — hold it!" : offCents < 0 ? "A little flat — aim higher" : "A little sharp — aim lower"}
        </p>
      </div>
      <div className="relative h-3 rounded-full bg-[#E5DEC9]">
        <div className="absolute inset-y-0 left-1/2 w-10 -ml-5 rounded-full bg-[#2D6A4F]/30" aria-hidden />
        <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow"
          style={{ left: `calc(${50 + clamped}% - 8px)`, background: inTune ? "#2D6A4F" : "#C69B3D", transition: "left 90ms linear" }}
          data-testid="pitch-needle" aria-hidden />
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-widest text-[#566270]"><span>flat</span><span>in tune</span><span>sharp</span></div>
    </div>
  );
}

export function PitchMatchActivity({ s, idx, onWin, refA4 = DEFAULT_A4 }) {
  const { pitch, error, start, stop, listening } = usePitch();
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(false);
  const hitStart = useRef(0);
  useEffect(() => {
    if (!listening || !pitch || done) return;
    if (Math.abs(centsFromTarget(pitch.freq, s.target_midi, refA4)) <= TUNE_TOL * 100) {
      if (!hitStart.current) hitStart.current = performance.now();
      if (performance.now() - hitStart.current > 700) { setDone(true); stop(); onWin?.(); }
    } else hitStart.current = 0;
  }, [pitch, listening, done]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const hear = async () => {
    setPlaying(true);
    try { await playAudioSpec({ type: "melody_vex", notes: [{ keys: s.cue_keys, dur: "w" }] }); } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`pitch-match-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="flex items-center gap-3 flex-wrap">
        <StaveView notation={{ clef: "treble", notes: [{ keys: s.cue_keys, dur: "w" }] }} width={180} height={110} testid={`pitch-match-stave-${idx}`} />
        <button onClick={hear} disabled={playing} data-testid={`pitch-match-hear-${idx}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
          <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : "Hear the note"}
        </button>
      </div>
      {!listening && !done && <MicGate error={error} start={start} testid={`pitch-match-${idx}`} />}
      {listening && (
        <>
          <PitchGauge pitch={pitch} targetMidi={s.target_midi} refA4={refA4} />
          <button onClick={stop} data-testid={`pitch-match-stop-${idx}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[44px]">
            <MicOff className="w-4 h-4" aria-hidden /> Stop the microphone
          </button>
        </>
      )}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`pitch-match-done-${idx}`}>In tune and steady — lovely singing!</p>}
    </div>
  );
}

export function SingIntervalActivity({ s, idx, onWin, refA4 = DEFAULT_A4 }) {
  // The stave shows ONLY the start note; the target note stays hidden until sung in tune.
  const { pitch, error, start, stop, listening } = usePitch();
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(false);
  const hitStart = useRef(0);
  useEffect(() => {
    if (!listening || !pitch || done) return;
    if (Math.abs(centsFromTarget(pitch.freq, s.target_midi, refA4)) <= TUNE_TOL * 100) {
      if (!hitStart.current) hitStart.current = performance.now();
      if (performance.now() - hitStart.current > 700) { setDone(true); stop(); onWin?.(); }
    } else hitStart.current = 0;
  }, [pitch, listening, done]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const hear = async () => {
    setPlaying(true);
    try { await playAudioSpec(s.audio); } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`sing-interval-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="flex items-center gap-3 flex-wrap">
        <StaveView notation={{ clef: "treble", notes: [{ keys: s.start_keys, dur: "w" }] }} width={180} height={110} testid={`sing-interval-stave-${idx}`} />
        <button onClick={hear} disabled={playing || listening} data-testid={`sing-interval-hear-${idx}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
          <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : listening ? "Keep singing!" : "Hear the start note"}
        </button>
      </div>
      <p className="text-sm font-bold text-[#7A1C2E]" data-testid={`sing-interval-task-${idx}`}>
        Your task: sing the note a {s.interval_label} above — {done ? `it was ${midiName(s.target_midi)}!` : "which note? That's for your ears to find."}
      </p>
      {!listening && !done && <MicGate error={error} start={start} testid={`sing-interval-${idx}`} />}
      {listening && !done && (
        <>
          <PitchGauge pitch={pitch} targetMidi={s.target_midi} refA4={refA4} />
          <button onClick={stop} data-testid={`sing-interval-stop-${idx}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[44px]">
            <MicOff className="w-4 h-4" aria-hidden /> Stop the microphone
          </button>
        </>
      )}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`sing-interval-done-${idx}`}>Lovely — you found the {s.interval_label} all by yourself!</p>}
    </div>
  );
}

export function MatchMyPitchActivity({ s, idx, onWin, refA4 = DEFAULT_A4 }) {
  // Hear-then-sing with NO notation: pure aural echo of a single note.
  const { pitch, error, start, stop, listening } = usePitch();
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(false);
  const hitStart = useRef(0);
  useEffect(() => {
    if (!listening || !pitch || done) return;
    if (Math.abs(centsFromTarget(pitch.freq, s.target_midi, refA4)) <= TUNE_TOL * 100) {
      if (!hitStart.current) hitStart.current = performance.now();
      if (performance.now() - hitStart.current > 700) { setDone(true); stop(); onWin?.(); }
    } else hitStart.current = 0;
  }, [pitch, listening, done]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const hear = async () => {
    setPlaying(true);
    try { await playAudioSpec(s.audio); } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`match-my-pitch-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <button onClick={hear} disabled={playing || listening} data-testid={`match-my-pitch-hear-${idx}`}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
        <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : listening ? "Keep singing!" : "Hear the note"}
      </button>
      {!listening && !done && <MicGate error={error} start={start} testid={`match-my-pitch-${idx}`} />}
      {listening && !done && (
        <>
          <PitchGauge pitch={pitch} targetMidi={s.target_midi} refA4={refA4} />
          <button onClick={stop} data-testid={`match-my-pitch-stop-${idx}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[44px]">
            <MicOff className="w-4 h-4" aria-hidden /> Stop the microphone
          </button>
        </>
      )}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`match-my-pitch-done-${idx}`}>Perfect echo — you matched my pitch!</p>}
    </div>
  );
}

export function SingPhraseActivity({ s, idx, onWin, refA4 = DEFAULT_A4 }) {
  const { pitch, error, start, stop, listening } = usePitch();
  const [pos, setPos] = useState(0);
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(false);
  const wrapRef = useRef(null);
  const hold = useRef(0);
  const targets = (s.phrase_notes || []).filter((n) => !n.dur.endsWith("r")).map((n) => vexKeyToMidi(n.keys[0]));
  useEffect(() => { // highlight progress on the live SVG (VexFlow may redraw any time)
    const nodes = wrapRef.current?.querySelectorAll(".vf-stavenote") || [];
    nodes.forEach((nd, i) => {
      nd.style.opacity = i === pos && !done ? "1" : "0.45";
      nd.querySelectorAll("path").forEach((p) => { if (i < pos || done) { p.style.fill = "#2D6A4F"; p.style.stroke = "#2D6A4F"; } });
    });
  });
  useEffect(() => {
    if (!listening || !pitch || done) return;
    const t = targets[pos];
    if (t == null) return;
    if (Math.abs(centsFromTarget(pitch.freq, t, refA4)) <= 50) {
      hold.current += 1;
      if (hold.current >= 3) {
        hold.current = 0;
        if (pos + 1 >= targets.length) { setDone(true); stop(); onWin?.(); } else setPos(pos + 1);
      }
    } else hold.current = 0;
  }, [pitch]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const intro = async () => {
    setPlaying(true);
    try {
      await playAudioSpec({ type: "chord", midis: [s.intro_midi - 12, s.intro_midi, s.intro_midi + 7] });
      await playAudioSpec({ type: "melody_vex", notes: [{ keys: s.phrase_notes[0].keys, dur: "h" }] });
    } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  const retry = () => { setPos(0); setDone(false); hold.current = 0; };
  const dirCents = pitch && targets[pos] != null ? centsFromTarget(pitch.freq, targets[pos], refA4) : 0;
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`sing-phrase-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div ref={wrapRef}><StaveView notation={s.notation} width={Math.min(620, window.innerWidth - 64)} testid={`sing-phrase-stave-${idx}`} /></div>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={intro} disabled={playing} data-testid={`sing-phrase-intro-${idx}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
          <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : "Hear your introduction"}
        </button>
        <div className="flex gap-1.5" aria-hidden>
          {targets.map((_, i) => (
            <span key={i} className={`w-2.5 h-2.5 rounded-full ${i < pos || done ? "bg-[#2D6A4F]" : i === pos ? "bg-[#C69B3D]" : "bg-[#E5DEC9]"}`} data-testid={`sing-phrase-dot-${idx}-${i}`} />
          ))}
        </div>
      </div>
      {!listening && !done && <MicGate error={error} start={start} testid={`sing-phrase-${idx}`} />}
      {listening && !done && (
        <div className="space-y-2">
          <p className="text-sm font-bold text-[#0B192C]" data-testid={`sing-phrase-status-${idx}`}>
            Sing note {pos + 1} of {targets.length}: <span className="font-display text-lg">{midiName(targets[pos])}</span>
            {pitch && <span className={Math.abs(dirCents) <= 50 ? "text-[#2D6A4F]" : "text-[#A62B2B]"}> — {Math.abs(dirCents) <= 50 ? "lovely!" : dirCents < 0 ? "higher…" : "lower…"}</span>}
          </p>
          <PitchGauge pitch={pitch} targetMidi={targets[pos]} refA4={refA4} />
          <button onClick={stop} data-testid={`sing-phrase-stop-${idx}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[44px]">
            <MicOff className="w-4 h-4" aria-hidden /> Stop the microphone
          </button>
        </div>
      )}
      {done && (
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`sing-phrase-done-${idx}`}>You sang the whole phrase!</p>
          <button onClick={retry} data-testid={`sing-phrase-retry-${idx}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[44px]">
            <RotateCcw className="w-4 h-4" aria-hidden /> Sing it again
          </button>
        </div>
      )}
    </div>
  );
}

export function FindWrongNoteActivity({ s, idx, onWin }) {
  const wrapRef = useRef(null);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { // delegated taps with generous forgiving hit targets (small noteheads are hard to tap)
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const onTap = (e) => {
      if (done) return;
      const nodes = [...wrap.querySelectorAll(".vf-stavenote")];
      if (!nodes.length) return;
      let best = -1;
      let bestD = Infinity;
      nodes.forEach((nd, i) => {
        const r = nd.getBoundingClientRect();
        const d = Math.hypot(e.clientX - (r.left + r.width / 2), (e.clientY - (r.top + r.height / 2)) * 1.6);
        if (d < bestD) { bestD = d; best = i; }
      });
      if (best >= 0 && bestD < 90) {
        setPicked(best);
        if (best === s.wrong_index) { setDone(true); onWin?.(); }
      }
    };
    wrap.addEventListener("click", onTap);
    return () => wrap.removeEventListener("click", onTap);
  }, [done, s.wrong_index]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { // paint the picked note; re-query live DOM after every render
    const nodes = wrapRef.current?.querySelectorAll(".vf-stavenote") || [];
    nodes.forEach((nd, i) => {
      nd.style.cursor = done ? "default" : "pointer";
      nd.querySelectorAll("path").forEach((p) => {
        const col = i !== picked ? "" : i === s.wrong_index && done ? "#2D6A4F" : "#A62B2B";
        p.style.fill = col;
        p.style.stroke = col;
      });
    });
  });
  const hear = async () => {
    setPlaying(true);
    try { await playAudioSpec(s.audio); } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`wrong-note-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <button onClick={hear} disabled={playing} data-testid={`wrong-note-hear-${idx}`}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
        <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : "▶ Play it (one note is wrong!)"}
      </button>
      <div ref={wrapRef} data-testid={`wrong-note-stave-${idx}`}>
        <StaveView notation={s.notation} width={Math.min(620, window.innerWidth - 64)} testid={`wrong-note-notation-${idx}`} />
      </div>
      {picked !== null && !done && (
        <p className="text-sm font-bold text-[#A62B2B]" data-testid={`wrong-note-miss-${idx}`}>Not that one — listen again and tap a different note.</p>
      )}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`wrong-note-done-${idx}`}>{s.feedback_correct || "Sharp ears — that was the wrong note!"}</p>}
    </div>
  );
}

export function SameDiffActivity({ s, idx, onWin }) {
  const [picked, setPicked] = useState(null);
  const [playing, setPlaying] = useState(false);
  const done = picked !== null && (picked === "same") === s.is_same;
  const pick = (v) => {
    if (done) return;
    setPicked(v);
    if ((v === "same") === s.is_same) onWin?.();
  };
  const hear = async () => {
    setPlaying(true);
    try { await playAudioSpec(s.audio); } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`same-diff-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <StaveView notation={s.notation} width={Math.min(620, window.innerWidth - 64)} testid={`same-diff-stave-${idx}`} />
      <button onClick={hear} disabled={playing} data-testid={`same-diff-hear-${idx}`}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
        <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : "▶ Listen"}
      </button>
      <div className="flex gap-3">
        {[["same", "The same"], ["diff", "Different!"]].map(([v, label]) => (
          <button key={v} onClick={() => pick(v)} disabled={done} data-testid={`same-diff-${v}-${idx}`}
            className={`px-5 py-3 rounded-xl border-2 font-bold text-sm min-h-[48px] transition-colors ${
              picked === v ? (done ? "border-[#2D6A4F] bg-[#EBF6F0]" : "border-[#A62B2B] bg-[#FBF0F2]")
                : "border-[#E5DEC9] bg-white hover:border-[#C69B3D]"}`}>{label}</button>
        ))}
      </div>
      {picked !== null && !done && <p className="text-sm font-bold text-[#A62B2B]" data-testid={`same-diff-miss-${idx}`}>Not quite — listen once more, watching the notes.</p>}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`same-diff-done-${idx}`}>{s.is_same ? "Right — note for note identical." : "Right — one note was changed. Good ears!"}</p>}
    </div>
  );
}

export function NotationPickActivity({ s, idx, onWin }) {
  const [picked, setPicked] = useState(null);
  const [playing, setPlaying] = useState(false);
  const done = picked !== null && picked === s.correct_index;
  const pick = (i) => {
    if (done) return;
    setPicked(i);
    if (i === s.correct_index) onWin?.();
  };
  const hear = async () => {
    setPlaying(true);
    try { await playAudioSpec(s.audio); } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`notation-pick-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.prompt}</p>
      {s.audio && (
        <button onClick={hear} disabled={playing} data-testid={`notation-pick-hear-${idx}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
          <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : "▶ Listen first"}
        </button>
      )}
      {s.notation && <StaveView notation={s.notation} width={Math.min(560, window.innerWidth - 64)} testid={`notation-pick-main-${idx}`} />}
      <div className="flex flex-wrap gap-3">
        {s.options.map((opt, i) => (
          <button key={i} onClick={() => pick(i)} disabled={done} data-testid={`notation-pick-option-${idx}-${i}`}
            className={`rounded-xl border-2 p-1 transition-colors min-h-[48px] ${
              picked === i ? (i === s.correct_index ? "border-[#2D6A4F] bg-[#EBF6F0]" : "border-[#A62B2B] bg-[#FBF0F2]")
                : "border-[#E5DEC9] bg-white hover:border-[#C69B3D]"}`}>
            <StaveView notation={opt} width={200} height={105} testid={`notation-pick-stave-${idx}-${i}`} />
          </button>
        ))}
      </div>
      {picked !== null && !done && (
        <p className="text-sm font-bold text-[#A62B2B]" data-testid={`notation-pick-miss-${idx}`}>Not quite — look carefully and try another.</p>
      )}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`notation-pick-done-${idx}`}>{s.why || "Exactly right!"}</p>}
    </div>
  );
}

export function QuickPickActivity({ s, idx, onWin }) {
  const [picked, setPicked] = useState(null);
  const [playing, setPlaying] = useState(false);
  const done = picked !== null && picked === s.correct_index;
  const pick = (i) => {
    if (done) return;
    setPicked(i);
    if (i === s.correct_index) onWin?.();
  };
  const hear = async () => {
    setPlaying(true);
    try { await playAudioSpec(s.audio); } catch { /* surfaced by audio lib */ }
    setPlaying(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`quick-pick-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.prompt}</p>
      {s.audio && (
        <button onClick={hear} disabled={playing} data-testid={`quick-pick-hear-${idx}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-50">
          <Volume2 className="w-4 h-4" aria-hidden /> {playing ? "Playing…" : "▶ Listen"}
        </button>
      )}
      {s.notation && <StaveView notation={s.notation} width={Math.min(560, window.innerWidth - 64)} testid={`quick-pick-stave-${idx}`} />}
      <div className="flex flex-wrap gap-3">
        {s.options.map((opt, i) => (
          <button key={i} onClick={() => pick(i)} disabled={done} data-testid={`quick-pick-option-${idx}-${i}`}
            className={`px-5 py-3 rounded-xl border-2 font-bold text-sm min-h-[48px] transition-colors ${
              picked === i ? (i === s.correct_index ? "border-[#2D6A4F] bg-[#EBF6F0]" : "border-[#A62B2B] bg-[#FBF0F2]")
                : "border-[#E5DEC9] bg-white hover:border-[#C69B3D]"}`}>{opt}</button>
        ))}
      </div>
      {picked !== null && !done && (
        <p className="text-sm font-bold text-[#A62B2B]" data-testid={`quick-pick-miss-${idx}`}>Not quite — {s.audio ? "listen" : "look"} once more and try another.</p>
      )}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`quick-pick-done-${idx}`}>{s.why || "Exactly right!"}</p>}
    </div>
  );
}

export function MatchCardsActivity({ s, idx, onWin }) {
  const pairs = s.pairs || [];
  const [order] = useState(() => {
    const idxs = pairs.map((_, i) => i);
    for (let i = idxs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idxs[i], idxs[j]] = [idxs[j], idxs[i]];
    }
    return idxs;
  });
  const [sel, setSel] = useState(null);
  const [matched, setMatched] = useState([]);
  const [wrong, setWrong] = useState(null);
  const done = pairs.length > 0 && matched.length === pairs.length;
  const won = useRef(false);
  useEffect(() => {
    if (done && !won.current) { won.current = true; onWin?.(); }
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickMeaning = (mi) => {
    if (sel === null || matched.includes(mi)) return;
    if (mi === sel) {
      setMatched((m) => [...m, mi]);
      setSel(null);
    } else {
      setWrong(mi);
      setTimeout(() => setWrong(null), 700);
    }
  };
  const cardCls = (i, isSel, isWrong) =>
    `w-full text-left px-3 py-3 rounded-xl border-2 text-sm font-bold min-h-[48px] transition-colors ${
      matched.includes(i) ? "border-[#2D6A4F] bg-[#EBF6F0] text-[#2D6A4F]"
        : isWrong ? "border-[#A62B2B] bg-[#FBF0F2]"
        : isSel ? "border-[#C69B3D] bg-[#F6EED8]"
        : "border-[#E5DEC9] bg-white hover:border-[#C69B3D]"}`;
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`term-match-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          {pairs.map(([term], i) => (
            <button key={`t${i}`} onClick={() => !matched.includes(i) && setSel(i)} disabled={matched.includes(i)}
              data-testid={`term-match-term-${idx}-${i}`} className={cardCls(i, sel === i, false)}>{term}</button>
          ))}
        </div>
        <div className="space-y-2">
          {order.map((mi) => (
            <button key={`m${mi}`} onClick={() => pickMeaning(mi)} disabled={matched.includes(mi)}
              data-testid={`term-match-meaning-${idx}-${mi}`} className={cardCls(mi, false, wrong === mi)}>{pairs[mi][1]}</button>
          ))}
        </div>
      </div>
      {wrong !== null && <p className="text-sm font-bold text-[#A62B2B]" data-testid={`term-match-miss-${idx}`}>Not that pair — try a different partner.</p>}
      {done && <p className="text-sm font-bold text-[#2D6A4F]" data-testid={`term-match-done-${idx}`}>Every pair matched — you really know these!</p>}
    </div>
  );
}
